// packages/portal/src/hooks/usePermission.js
//
// ⚠ THIS IS NOW AN ADAPTER, NOT A SECOND PERMISSION ENGINE.
//
// Spec: specs/20260824-rights-consistency-audit/spec.md §1 (Step 0).
//
// ── WHAT IT USED TO DO, AND WHY THAT WAS A HOLE ─────────────────────────────
//
// This hook carried its own copy of the permission rule, reading `user.permissions`
// straight off AuthContext:
//
//     if (['admin', 'company_admin'].includes(user.role)) return true;
//
// with NO `PROTECTED_PERMISSIONS` check. It backs `PermissionRoute`,
// `CompanyPermissionRoute` and `PermissionGate` — i.e. every route guard in App.js —
// so a `company_admin` passed every guard, including `modules.manage`,
// `onboarding.view`, `templates.manage` and `pos.report_persons`. Every GDPR carve-out
// in the catalog was a no-op for route access. The nav hid those entries, which made
// the pages invisible *and still reachable by URL*.
//
// `PermissionContext` had the rule right all along. Two engines, one correct.
//
// ── WHY AN ADAPTER RATHER THAN A DELETION ───────────────────────────────────
//
// The obvious fix is to delete this file and point everything at
// `usePermissions()`. That is not a drop-in: this hook takes
// `(application, permission)` and 9 of its 11 consumers call
// `hasPermission(null, 'catalog.manage')`. `PermissionContext.hasPermission` treats a
// second argument as a suffix and would build `"null.catalog.manage"` — every one of
// those checks would silently return false, closing pages that should be open.
//
// So the signature stays and the RULE moves. One engine, two calling conventions.
// Converging the call sites is a follow-up refactor with no security content.
//
// ── A BUG THIS ALSO FIXES ───────────────────────────────────────────────────
//
// `BusinessUpdate/index.jsx:1681` and `SalesPresentation/index.jsx:1223` call
// `hasPermission('system.admin')` with ONE argument. Under the old implementation
// that produced the key `"system.admin.undefined"`, so it was always false for
// ordinary roles and always true for full-access roles — the check happened to look
// right while consulting nothing. `normaliseKey` below handles the one-argument form,
// so an account explicitly granted `system.admin` now actually gets it.

import { usePermissions } from '../contexts/PermissionContext';

/**
 * Collapse the legacy `(application, permission)` signature into a single key.
 *
 *   ('system.admin')                  -> 'system.admin'      (one-arg form)
 *   (null, 'catalog.manage')          -> 'catalog.manage'    (the common form)
 *   ('API-portal', 'devices.view')    -> 'API-portal.devices.view'
 *
 * The prefixed form is retained only for compatibility; the 2026-04-28 refactor
 * dropped every `API-*` prefix from the catalog, so nothing should rely on it.
 */
const normaliseKey = (application, permission) => {
  if (permission === undefined || permission === null) return application;
  if (application === undefined || application === null || application === '') {
    return permission;
  }
  return `${application}.${permission}`;
};

export const usePermission = () => {
  const {
    hasPermission: ctxHasPermission,
    permissions,
    loading,
    error,
  } = usePermissions();

  const hasPermission = (application, permission) =>
    // Single-argument call into the context: it is the PROTECTED-aware path.
    ctxHasPermission(normaliseKey(application, permission));

  const hasAnyPermission = (application, list = []) =>
    list.some((p) => hasPermission(application, p));

  const hasAllPermissions = (application, list = []) =>
    list.every((p) => hasPermission(application, p));

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Exposed so route guards can wait instead of redirecting mid-fetch — see
    // PermissionRoute. Without this, a page refresh bounces the user to /dashboard
    // before their permissions have arrived.
    permissionsLoading: loading,
    permissionsError: error,
    permissions,
  };
};

export default usePermission;
